// components/NewPOSModal.jsx
import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  createPOSOrder, loadPOSItems, signPOSItemImages, decrementPOSInventory,
  loadPOSDeliveryContacts, createPOSDeliveryContact,
} from "../services/posService";
import "./NewPOSModal.css";

const EMPTY_ORDER = {
  submitterName: "", submitterEmail: "",
  shipToCompany: "", shipToAddress: "",
  shipToContactName: "", shipToContactPhone: "", shipToContactEmail: "",
  orderDate: "", requestedShipDate: "", specialInstructions: "",
  notifyDistributor: true,
};

const EMPTY_NEW_WH = {
  state: "", distributor: "", warehouseName: "",
  posStreetAddress: "", posCity: "", posZipcode: "",
  posContactName: "", posContactPhone: "", posContactEmail: "",
};

const REQUIRED_FIELDS = [
  { key: "submitterName",     label: "Submitter Name" },
  { key: "submitterEmail",    label: "Submitter Email" },
  { key: "orderDate",         label: "Order Date" },
  { key: "requestedShipDate", label: "Requested Ship Date" },
];

export default function NewPOSModal({ user, onClose, onCreated }) {
  const [posItems,         setPosItems]         = useState([]);
  const [itemsLoading,     setItemsLoading]     = useState(true);
  const [deliveryContacts, setDeliveryContacts] = useState([]);
  const [contactsLoading,  setContactsLoading]  = useState(true);

  const [selectedState,     setSelectedState]     = useState("");
  const [selectedDist,      setSelectedDist]      = useState("");
  const [selectedContactId, setSelectedContactId] = useState(null);

  const [showNewWH,   setShowNewWH]   = useState(false);
  const [newWH,       setNewWH]       = useState(EMPTY_NEW_WH);
  const [newWHSaving, setNewWHSaving] = useState(false);
  const [newWHError,  setNewWHError]  = useState(null);

  const [order,  setOrder]  = useState({
    ...EMPTY_ORDER,
    submitterName:  user?.displayName || "",
    submitterEmail: user?.mail || user?.username || "",
    orderDate: new Date().toISOString().slice(0, 10),
  });
  const [qtys,   setQtys]   = useState({});
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);
  const [tab,    setTab]    = useState("order");

  useEffect(() => {
    // Item images live in a private, per-tenant bucket (migration 0069), so
    // batch-resolve signed URLs before render instead of using image_url directly.
    loadPOSItems()
      // `loadPOSItems` returns the whole catalog so System Settings can manage
      // retired items. An ORDER form must only offer live ones — the public
      // embed has always filtered on `active`, and this modal did not, so
      // deactivating an item hid it from the distributor's form while the brand
      // could still order it here.
      .then(rows => rows.filter(r => r.active !== false))
      .then(signPOSItemImages)
      .then(rows => {
        setPosItems(rows);
        setQtys(Object.fromEntries(rows.map(r => [r.item_key, ""])));
      })
      .catch(err => setError(err.message))
      .finally(() => setItemsLoading(false));

    loadPOSDeliveryContacts()
      .then(rows => setDeliveryContacts(rows))
      .catch(err => setError(err.message))
      .finally(() => setContactsLoading(false));
  }, []);

  const setO  = (key, val) => setOrder(prev => ({ ...prev, [key]: val }));
  const setQ  = (key, val) => setQtys(prev => ({ ...prev, [key]: val }));
  const setWH = (key, val) => setNewWH(prev => ({ ...prev, [key]: val }));

  const itemTotal = posItems.reduce((sum, r) => sum + (parseInt(qtys[r.item_key], 10) || 0), 0);

  // ── Derived dropdown options ────────────────────────────────────────────────
  const stateOptions = [...new Set(deliveryContacts.map(c => c.state).filter(Boolean))].sort();
  const distOptions  = [...new Set(
    deliveryContacts
      .filter(c => !selectedState || c.state === selectedState)
      .map(c => c.distributor)
      .filter(Boolean)
  )].sort();
  const warehouseOptions = deliveryContacts.filter(c =>
    (!selectedState || c.state === selectedState) &&
    (!selectedDist  || c.distributor === selectedDist)
  );

  // ── Apply a selected contact to the order ──────────────────────────────────
  const applyContact = (contact) => {
    if (!contact) return;
    setSelectedContactId(contact.id);
    const addrParts = [contact.pos_street_address, contact.pos_city, contact.pos_zipcode].filter(Boolean);
    setOrder(prev => ({
      ...prev,
      shipToCompany:      contact.warehouse_name   || "",
      shipToAddress:      addrParts.join(", "),
      shipToContactName:  contact.pos_contact_name  || "",
      shipToContactPhone: contact.pos_contact_phone || "",
      shipToContactEmail: contact.pos_contact_email || "",
    }));
  };

  const clearShipTo = () => {
    setSelectedContactId(null);
    setOrder(prev => ({
      ...prev,
      shipToCompany: "", shipToAddress: "",
      shipToContactName: "", shipToContactPhone: "", shipToContactEmail: "",
    }));
  };

  const handleStateChange = (state) => {
    setSelectedState(state);
    setSelectedDist("");
    clearShipTo();
  };

  const handleDistChange = (dist) => {
    setSelectedDist(dist);
    clearShipTo();
  };

  const handleWarehouseChange = (id) => {
    if (!id) { clearShipTo(); return; }
    applyContact(deliveryContacts.find(c => String(c.id) === id) ?? null);
  };

  // ── Sub-modal derived options ──────────────────────────────────────────────
  const newWHStateOptions = stateOptions; // reuse already-derived list
  const newWHDistOptions = [...new Set(
    deliveryContacts
      .filter(c => !newWH.state || c.state === newWH.state)
      .map(c => c.distributor)
      .filter(Boolean)
  )].sort();

  // ── Save new warehouse ─────────────────────────────────────────────────────
  const handleSaveNewWH = async () => {
    if (!newWH.state || !newWH.warehouseName) {
      setNewWHError("State and Warehouse Name are required.");
      return;
    }
    if (!newWH.posStreetAddress || !newWH.posCity || !newWH.posZipcode) {
      setNewWHError("Street Address, City, and Zip Code are required.");
      return;
    }
    if (!newWH.posContactEmail) {
      setNewWHError("Contact Email is required.");
      return;
    }
    setNewWHSaving(true); setNewWHError(null);
    try {
      const created = await createPOSDeliveryContact(newWH);
      setDeliveryContacts(prev =>
        [...prev, created].sort((a, b) => a.state.localeCompare(b.state) || (a.distributor || "").localeCompare(b.distributor || ""))
      );
      setSelectedState(created.state);
      setSelectedDist(created.distributor || "");
      applyContact(created);
      setShowNewWH(false);
      setNewWH(EMPTY_NEW_WH);
    } catch (err) {
      setNewWHError(err.message);
    } finally {
      setNewWHSaving(false);
    }
  };

  // ── Submit order ───────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    for (const { key, label } of REQUIRED_FIELDS) {
      if (!order[key]?.toString().trim()) {
        setError(`${label} is required.`);
        setTab("order");
        return;
      }
    }
    if (!selectedContactId) {
      setError("Please select a POS warehouse location.");
      setTab("order");
      return;
    }
    if (itemTotal === 0) {
      setError("Please enter a quantity greater than 0 for at least one POS item.");
      setTab("items");
      return;
    }
    // The per-item minimum is a tenant setting that `pos-order-public` has
    // always enforced. This path did not, so the same order was accepted in the
    // app and rejected on the distributor's own form.
    for (const item of posItems) {
      const qty = parseInt(qtys[item.item_key], 10) || 0;
      const min = item.min_order_quantity ?? 1;
      if (qty > 0 && qty < min) {
        setError(`${item.name} has a minimum order of ${min}.`);
        setTab("items");
        return;
      }
    }
    setError(null);
    setSaving(true);
    try {
      const dbOrder = await createPOSOrder(order, qtys);
      decrementPOSInventory(posItems, qtys).catch(console.error);
      onCreated({
        id:                  dbOrder.id,
        orderNumber:         dbOrder.order_number        ?? "",
        status:              dbOrder.status              ?? "Pending",
        submitterName:       order.submitterName,
        submitterEmail:      order.submitterEmail,
        shipToCompany:       order.shipToCompany,
        shipToAddress:       order.shipToAddress,
        shipToContactName:   order.shipToContactName,
        shipToContactPhone:  order.shipToContactPhone,
        shipToContactEmail:  order.shipToContactEmail,
        orderDate:           order.orderDate,
        requestedShipDate:   order.requestedShipDate,
        specialInstructions: order.specialInstructions,
        shipDate: "", estDelivery: "", carrier: "", tracking: "",
        createdAt: dbOrder.created_at ?? "",
        items: Object.fromEntries(
          posItems.map(r => [r.item_key, parseInt(qtys[r.item_key], 10) || 0])
        ),
      });
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  const selectedContact = deliveryContacts.find(c => c.id === selectedContactId) ?? null;

  // Portaled to <body> so the fixed backdrop escapes the app shell's stacking
  // context (`.appshell` z-index:1) and renders ABOVE the sticky topnav
  // (z-index:50 + backdrop-filter). Otherwise the modal's top is painted behind
  // the topnav and looks clipped.
  return createPortal(
    // `.pos-module` re-supplies the the source app→BC CSS-var bridge (--navy-mid, --border,
    // --amber, …) which is scoped to .pos-module — without it the portaled modal
    // loses those tokens and the panel background renders transparent.
    <div className="pos-module">
      {/* No backdrop click-to-close — users were losing in-progress POS orders
          when they accidentally clicked outside the modal. Close via the X /
          Cancel buttons only. */}
      <div className="npm-backdrop">
        <div className="npm-modal">

          <div className="npm-header">
            <div className="npm-header-left">
              <h2 className="npm-title">New POS Order</h2>
              {itemTotal > 0 && <span className="npm-item-badge">{itemTotal} items</span>}
            </div>
            <button className="npm-close" onClick={onClose}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>

          <div className="npm-tabs">
            <button className={`npm-tab${tab === "order" ? " npm-tab-active" : ""}`} onClick={() => setTab("order")}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
              </svg>
              Order Info
            </button>
            <button className={`npm-tab${tab === "items" ? " npm-tab-active" : ""}`} onClick={() => setTab("items")}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/>
              </svg>
              POS Items
              {itemTotal > 0 && <span className="npm-tab-count">{itemTotal}</span>}
            </button>
          </div>

          <div className="npm-body">
            {error && <div className="npm-error">{error}</div>}

            {/* ── Order Info tab ── */}
            {tab === "order" && (
              <>
                <div className="npm-section-title">Submitter</div>
                <div className="npm-row">
                  <div className="npm-field npm-grow">
                    <label>Submitter Name <span className="npm-required">*</span></label>
                    <input value={order.submitterName} onChange={e => setO("submitterName", e.target.value)} placeholder="Your name" />
                  </div>
                  <div className="npm-field npm-grow">
                    <label>Submitter Email <span className="npm-required">*</span></label>
                    <input value={order.submitterEmail} onChange={e => setO("submitterEmail", e.target.value)} placeholder="your@email.com" />
                  </div>
                </div>

                <div className="npm-section-title">Ship To</div>
                {contactsLoading ? (
                  <div className="npm-items-loading">Loading warehouse locations…</div>
                ) : (
                  <>
                    {/* Cascading selects */}
                    <div className="npm-row">
                      <div className="npm-field npm-grow">
                        <label>State <span className="npm-required">*</span></label>
                        <select className="npm-select" value={selectedState} onChange={e => handleStateChange(e.target.value)}>
                          <option value="">— Select State —</option>
                          {stateOptions.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                      <div className="npm-field npm-grow">
                        <label>Distributor</label>
                        <select className="npm-select" value={selectedDist} onChange={e => handleDistChange(e.target.value)} disabled={!selectedState}>
                          <option value="">— Select Distributor —</option>
                          {distOptions.map(d => <option key={d} value={d}>{d}</option>)}
                        </select>
                      </div>
                      <div className="npm-field npm-grow">
                        <label>Warehouse <span className="npm-required">*</span></label>
                        <select
                          className="npm-select"
                          value={selectedContactId ?? ""}
                          onChange={e => handleWarehouseChange(e.target.value)}
                          disabled={!selectedState}
                        >
                          <option value="">— Select Warehouse —</option>
                          {warehouseOptions.map(c => (
                            <option key={c.id} value={c.id}>{c.warehouse_name}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Auto-filled address + contact display */}
                    {selectedContact ? (
                      <div className="npm-wh-display">
                        <div className="npm-wh-display-row">
                          <div className="npm-wh-display-field npm-grow">
                            <span className="npm-wh-display-label">Street Address</span>
                            <span className="npm-wh-display-value">{selectedContact.pos_street_address || <em>—</em>}</span>
                          </div>
                          <div className="npm-wh-display-field" style={{ minWidth: 130 }}>
                            <span className="npm-wh-display-label">City</span>
                            <span className="npm-wh-display-value">{selectedContact.pos_city || <em>—</em>}</span>
                          </div>
                          <div className="npm-wh-display-field" style={{ minWidth: 90 }}>
                            <span className="npm-wh-display-label">Zip</span>
                            <span className="npm-wh-display-value">{selectedContact.pos_zipcode || <em>—</em>}</span>
                          </div>
                        </div>
                        <div className="npm-wh-display-row">
                          <div className="npm-wh-display-field npm-grow">
                            <span className="npm-wh-display-label">Contact Name</span>
                            <span className="npm-wh-display-value">{selectedContact.pos_contact_name || <em>—</em>}</span>
                          </div>
                          <div className="npm-wh-display-field npm-grow">
                            <span className="npm-wh-display-label">Contact Phone</span>
                            <span className="npm-wh-display-value">{selectedContact.pos_contact_phone || <em>—</em>}</span>
                          </div>
                          <div className="npm-wh-display-field npm-grow">
                            <span className="npm-wh-display-label">Contact Email</span>
                            <span className="npm-wh-display-value">{selectedContact.pos_contact_email || <em>—</em>}</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="npm-wh-placeholder">
                        Select a state and warehouse above to populate address and contact details.
                      </div>
                    )}

                    {/* New warehouse button */}
                    <button className="npm-wh-new-btn" onClick={() => { setShowNewWH(true); setNewWH(EMPTY_NEW_WH); setNewWHError(null); }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                      </svg>
                      Enter a new POS warehouse
                    </button>
                  </>
                )}

                <div className="npm-notify-box">
                  <label className="npm-checkbox-label">
                    <input
                      type="checkbox"
                      className="npm-checkbox"
                      checked={order.notifyDistributor}
                      onChange={e => setO("notifyDistributor", e.target.checked)}
                    />
                    Send Distributor Ship To Contact Tracking Info
                  </label>
                </div>

                <div className="npm-section-title">Dates</div>
                <div className="npm-row">
                  <div className="npm-field">
                    <label>Order Date <span className="npm-required">*</span></label>
                    <input type="date" value={order.orderDate} onChange={e => setO("orderDate", e.target.value)} />
                  </div>
                  <div className="npm-field">
                    <label>Requested Ship Date <span className="npm-required">*</span></label>
                    <input type="date" value={order.requestedShipDate} onChange={e => setO("requestedShipDate", e.target.value)} />
                  </div>
                </div>

                <div className="npm-section-title">Notes</div>
                <div className="npm-row">
                  <div className="npm-field npm-grow">
                    <label>Special Instructions</label>
                    <textarea value={order.specialInstructions} onChange={e => setO("specialInstructions", e.target.value)} placeholder="Any special handling or delivery notes…" rows={3} />
                  </div>
                </div>
              </>
            )}

            {/* ── POS Items tab ── */}
            {tab === "items" && (
              <>
                <p className="npm-items-desc">Enter the quantity needed for each POS item. Leave blank or enter 0 for items not needed.</p>
                {itemsLoading ? (
                  <div className="npm-items-loading">Loading items…</div>
                ) : (
                  <div className="npm-items-grid">
                    {posItems.map(item => (
                      <div key={item.item_key} className="npm-item-row">
                        {item.image_src ? (
                          <img src={item.image_src} alt={item.name} className="npm-item-img" />
                        ) : (
                          <div className="npm-item-img-placeholder">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                              <circle cx="8.5" cy="8.5" r="1.5"/>
                              <polyline points="21 15 16 10 5 21"/>
                            </svg>
                          </div>
                        )}
                        <div className="npm-item-info">
                          <div className="npm-item-name">{item.name}</div>
                          {item.description && <div className="npm-item-desc">{item.description}</div>}
                          <div className="npm-item-meta">
                            <span className="npm-item-inventory">In Stock: <strong>{item.current_inventory ?? 0}</strong></span>
                            {(item.min_order_quantity ?? 1) > 1 && (
                              <span className="npm-item-min">Min order: <strong>{item.min_order_quantity}</strong></span>
                            )}
                            {item.price_per_unit != null && (
                              <span className="npm-item-price">Price/Unit: <strong>${Number(item.price_per_unit).toFixed(2)}</strong></span>
                            )}
                          </div>
                        </div>
                        <input
                          className="npm-item-input"
                          type="number"
                          min="0"
                          value={qtys[item.item_key] ?? ""}
                          onChange={e => setQ(item.item_key, e.target.value)}
                          placeholder="0"
                        />
                      </div>
                    ))}
                  </div>
                )}
                {itemTotal > 0 && (
                  <div className="npm-items-total">
                    Total items: <strong>{itemTotal}</strong>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="npm-footer">
            <button className="npm-cancel-btn" onClick={onClose} disabled={saving}>Cancel</button>
            <div className="npm-footer-right">
              {tab === "order" && (
                <button className="npm-next-btn" onClick={() => setTab("items")}>
                  Next: POS Items →
                </button>
              )}
              <button className="npm-submit-btn" onClick={handleSubmit} disabled={saving}>
                {saving ? "Submitting…" : "Submit Order"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── New Warehouse Sub-modal ── */}
      {showNewWH && (
        <div className="npm-submodal-backdrop" onClick={() => setShowNewWH(false)}>
          <div className="npm-submodal" onClick={e => e.stopPropagation()}>
            <div className="npm-submodal-header">
              <div className="npm-submodal-title">Add New POS Warehouse</div>
              <button className="npm-close" onClick={() => setShowNewWH(false)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="npm-submodal-body">
              {newWHError && <div className="npm-error">{newWHError}</div>}
              <div className="npm-row">
                <div className="npm-field npm-grow">
                  <label>State <span className="npm-required">*</span></label>
                  <select
                    className="npm-select"
                    value={newWH.state}
                    onChange={e => setNewWH(prev => ({ ...prev, state: e.target.value, distributor: "" }))}
                  >
                    <option value="">— Select State —</option>
                    {newWHStateOptions.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="npm-field npm-grow">
                  <label>Distributor</label>
                  <select
                    className="npm-select"
                    value={newWH.distributor}
                    onChange={e => setWH("distributor", e.target.value)}
                    disabled={!newWH.state}
                  >
                    <option value="">— Select Distributor —</option>
                    {newWHDistOptions.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
              </div>
              <div className="npm-field">
                <label>Warehouse Name <span className="npm-required">*</span></label>
                <input value={newWH.warehouseName} onChange={e => setWH("warehouseName", e.target.value)} placeholder="Warehouse name" />
              </div>
              <div className="npm-field" style={{ marginTop: 10 }}>
                <label>Street Address <span className="npm-required">*</span></label>
                <input value={newWH.posStreetAddress} onChange={e => setWH("posStreetAddress", e.target.value)} placeholder="Street address" />
              </div>
              <div className="npm-row" style={{ marginTop: 0 }}>
                <div className="npm-field npm-grow">
                  <label>City <span className="npm-required">*</span></label>
                  <input value={newWH.posCity} onChange={e => setWH("posCity", e.target.value)} placeholder="City" />
                </div>
                <div className="npm-field" style={{ minWidth: 110 }}>
                  <label>Zip Code <span className="npm-required">*</span></label>
                  <input value={newWH.posZipcode} onChange={e => setWH("posZipcode", e.target.value)} placeholder="Zip" />
                </div>
              </div>
              <div className="npm-field" style={{ marginTop: 10 }}>
                <label>Contact Name</label>
                <input value={newWH.posContactName} onChange={e => setWH("posContactName", e.target.value)} placeholder="Contact name" />
              </div>
              <div className="npm-row" style={{ marginTop: 0 }}>
                <div className="npm-field npm-grow">
                  <label>Contact Phone</label>
                  <input value={newWH.posContactPhone} onChange={e => setWH("posContactPhone", e.target.value)} placeholder="Phone number" />
                </div>
                <div className="npm-field npm-grow">
                  <label>Contact Email <span className="npm-required">*</span></label>
                  <input type="email" value={newWH.posContactEmail} onChange={e => setWH("posContactEmail", e.target.value)} placeholder="email@example.com" />
                </div>
              </div>
            </div>
            <div className="npm-submodal-footer">
              <button className="npm-cancel-btn" onClick={() => setShowNewWH(false)} disabled={newWHSaving}>Cancel</button>
              <button className="npm-submit-btn" onClick={handleSaveNewWH} disabled={newWHSaving}>
                {newWHSaving ? "Saving…" : "Save & Select"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}
